import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Card, Badge, DataTable } from "../components/ui";

const TIPO_IMPIANTO_OPTIONS = {
  100000000: "Fotovoltaico",
};

const TIPO_ICONS = {
  100000000: "☀️",
};

const STATO_OPTIONS = {
  100000000: { label: "Funzionante", variant: "success", icon: "✅" },
  100000001: { label: "Guasto", variant: "danger", icon: "❌" },
  100000002: { label: "In manutenzione", variant: "warning", icon: "🔧" },
  100000003: { label: "Dismesso", variant: "default", icon: "🚫" },
};

function ImpiantiListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, apiRequest } = useAuth();
  const { showToast } = useToast();

  const [impianti, setImpianti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rsaList, setRsaList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");

  const rsaIdFilter = searchParams.get("rsaId") || "";
  const tipoFilter = searchParams.get("tipo") || "";
  const statoFilter = searchParams.get("stato") || "";

  const isAdmin = user?.ruolo === 100000000;
  const isRspp = user?.ruolo === 100000001;
  const canCreate = isAdmin || isRspp;

  useEffect(() => {
    apiRequest("/rsa")
      .then((rsaData) => setRsaList(rsaData.value || []))
      .catch(() => {});
  }, [apiRequest]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (rsaIdFilter) params.append("rsaId", rsaIdFilter);
        if (tipoFilter) params.append("tipoImpianto", tipoFilter);
        if (statoFilter) params.append("stato", statoFilter);
        if (searchTerm) params.append("search", searchTerm);
        params.append("top", pageSize);
        params.append("skip", (page - 1) * pageSize);

        const data = await apiRequest(`/impianti?${params.toString()}`);
        setImpianti(data.value || []);
        setTotalCount(data.count || 0);
      } catch (error) {
        console.error("Error fetching impianti:", error);
        showToast("Errore nel caricamento degli impianti", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    apiRequest,
    rsaIdFilter,
    tipoFilter,
    statoFilter,
    page,
    pageSize,
    searchTerm,
    showToast,
  ]);

  const handleFilterChange = (key, value) => {
    setPage(1);
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const handleSearch = (term) => {
    setPage(1);
    setSearchTerm(term);
  };

  const handlePageSizeChange = (newSize) => {
    setPage(1);
    setPageSize(parseInt(newSize));
  };

  const columns = [
    {
      key: "tipo",
      label: "Tipo",
      render: (row) => (
        <span className="flex items-center gap-2">
          <span className="text-xl">
            {TIPO_ICONS[row.tipoImpianto] || "📦"}
          </span>
          <span className="text-sm text-gray-600">{row.tipoImpiantoLabel}</span>
        </span>
      ),
    },
    {
      key: "nome",
      label: "Nome",
      render: (row) => (
        <div>
          <Link
            to={`/impianti/${row.id}`}
            className="text-primary-600 hover:underline font-medium"
          >
            {row.nome}
          </Link>
          {row.marca && (
            <p className="text-xs text-gray-500">
              {row.marca} {row.modello}
            </p>
          )}
        </div>
      ),
    },
    {
      key: "potenza",
      label: "Potenza",
      render: (row) =>
        row.potenzaInstallata != null ? `${row.potenzaInstallata} kWp` : "-",
    },
    {
      key: "immobile",
      label: "Immobile",
      render: (row) =>
        row.immobileNome ? (
          <Link
            to={`/immobili/${row.immobileId}`}
            className="text-gray-600 hover:text-primary-600 text-sm"
          >
            {row.immobileNome}
          </Link>
        ) : (
          <span className="text-gray-400 text-sm">Non specificato</span>
        ),
    },
    {
      key: "rsa",
      label: "RSA",
      render: (row) => (
        <Link
          to={`/rsa/${row.rsaId}`}
          className="text-gray-600 hover:text-primary-600 text-sm"
        >
          {row.rsaNome}
        </Link>
      ),
    },
    {
      key: "stato",
      label: "Stato",
      render: (row) => {
        const statoInfo = STATO_OPTIONS[row.stato] || {
          label: "Sconosciuto",
          variant: "default",
        };
        return (
          <Badge variant={statoInfo.variant}>
            {statoInfo.icon} {statoInfo.label}
          </Badge>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Impianti</h1>
          <p className="text-gray-500 mt-1">
            Impianti tecnologici (fotovoltaico e futuri) delle RSA
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => navigate("/impianti/nuovo")}>
            + Nuovo Impianto
          </Button>
        )}
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              RSA
            </label>
            <select
              value={rsaIdFilter}
              onChange={(e) => handleFilterChange("rsaId", e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">Tutte le RSA</option>
              {rsaList.map((rsa) => (
                <option key={rsa.id} value={rsa.id}>
                  {rsa.nome}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo
            </label>
            <select
              value={tipoFilter}
              onChange={(e) => handleFilterChange("tipo", e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">Tutti i tipi</option>
              {Object.entries(TIPO_IMPIANTO_OPTIONS).map(([value, label]) => (
                <option key={value} value={value}>
                  {TIPO_ICONS[value]} {label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stato
            </label>
            <select
              value={statoFilter}
              onChange={(e) => handleFilterChange("stato", e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">Tutti gli stati</option>
              {Object.entries(STATO_OPTIONS).map(([value, info]) => (
                <option key={value} value={value}>
                  {info.icon} {info.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      <Card>
        <DataTable
          data={impianti}
          columns={columns}
          loading={loading}
          searchable
          searchPlaceholder="Cerca per nome, marca, numero pratica..."
          emptyMessage="Nessun impianto trovato"
          onRowClick={(row) => navigate(`/impianti/${row.id}`)}
          serverSide
          totalCount={totalCount}
          currentPage={page}
          onPageChange={setPage}
          onSearch={handleSearch}
          onPageSizeChange={handlePageSizeChange}
          pageSize={pageSize}
        />
      </Card>
    </div>
  );
}

export default ImpiantiListPage;
